import type { CartItem } from "@/components/microsite/types"
import type { SubstitutionPref } from "@/lib/online-orders/substitutionPrefs"

/** Una línea del carrito como la aceptan las rutas online (`onlineOrderItemSchema`). */
export type MicrositeOrderItem = {
  item_id: string
  quantity: number
  modifiers: CartItem["modifiers"]
  special_instructions?: string
  zone_layout?: CartItem["zoneLayout"]
  /** «Si no hay» de esta línea (spec sustituciones §4.2). undefined = hereda la de la orden. */
  substitution_pref?: SubstitutionPref
  substitution_item_id?: string
  /** Súper online: peso en milésimas de `items.weight_unit`. undefined = JSON lo omite. */
  weight_milli?: number
  /** Súper online: variante elegida. undefined = JSON lo omite. */
  item_variant_id?: string
}

/**
 * El carrito del micrositio, recortado a lo que viaja al servidor.
 *
 * Vive acá y no en `PaymentStep` porque tiene DOS llamadores que deben mandar los
 * MISMOS bytes: el POST a `/api/online-orders/session` (cotiza y cobra) y el POST de
 * la orden a `/api/online-orders` (asienta lo cobrado). Con el map escrito dos veces,
 * un campo agregado a uno y olvidado en el otro deja al cotizador y a la escritura
 * discrepando — y esa discrepancia es un 422 `amount_mismatch` DESPUÉS del cobro.
 */
export function buildMicrositeOrderItems(cart: readonly CartItem[]): MicrositeOrderItem[] {
  return cart.map((i) => ({
    item_id: i.itemId,
    quantity: i.quantity,
    modifiers: i.modifiers,
    special_instructions: i.specialInstructions,
    // Pizza por zonas: la disposición la decide la LÍNEA, no cada modificador. Sin esto
    // la pizza salía como `whole` con modificadores en "left"/"right" y las dos rutas la
    // rechazaban con 422 `zoned_selection_invalid` — el cliente armaba la mitad y mitad
    // en el micrositio y el checkout no lo dejaba pagar.
    //
    // El TAMAÑO no viaja acá: desde #2058 el motor lo deriva de los modificadores de la
    // línea (`resolveLineSize`, contra `items.zone_size_group_id` en la DB) y el campo
    // ya no existe en `onlineOrderItemSchema`. Mandarlo sería pasarle al servidor un
    // valor del cliente que el servidor decidió no volver a creer.
    //
    // Este recorte es campo por campo: un campo nuevo que decida precio o forma de la
    // línea hay que agregarlo acá a mano.
    zone_layout: i.zoneLayout,
    substitution_pref: i.substitutionPref,
    substitution_item_id: i.substitutionItemId,
    // Súper online: sin peso ni variante (todo restaurante) quedan undefined y
    // JSON.stringify no los escribe → mismos bytes que main.
    weight_milli: i.weightMilli,
    item_variant_id: i.variantId,
  }))
}
